/**
 * Rich text (or plain text) to a single line of readable plain text.
 *
 * Violation bodies, comments and other WYSIWYG columns live in DB columns that hold two shapes —
 * plaintext from the legacy textarea editors, HTML from the rich text editor — and nothing normalizes
 * them on write. Anywhere the app shows that content as text rather than rendering it (list previews,
 * CSV exports) it has to flatten it first, or the user sees `<p>`, `<b>` and `&nbsp;`.
 *
 * This is the TypeScript twin of the server-side CsvExportService::plainText(); the two are kept in
 * the same order deliberately, so the same row reads identically in the app and in an export.
 *
 * Normalizes only — truncation and CSV quoting stay with the caller.
 */

/**
 * Elements whose CONTENTS are markup rather than prose. Removing only the tags leaves a cell or a
 * preview holding a stylesheet: a Gmail or Outlook-web paste carries an uncommented `<style>` block,
 * and a whole-document paste carries `<title>`. Word's own paste wraps its stylesheet in an HTML
 * comment, which is why HTML_COMMENTS covered this until the uncommented form turned up.
 */
const RAW_TEXT_ELEMENTS = /<(style|script|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/**
 * Block-level elements, shared by the flattener (BLOCK_TAGS) and the render classifier (BLOCK_MARKUP)
 * so the two cannot drift apart.
 */
const BLOCK_ELEMENTS = 'br|p|div|li|ul|ol|tr|td|th|table|h[1-6]|blockquote';

/**
 * Tags whose boundaries carry meaning as whitespace — without this, removing tags fuses the words on
 * either side ("<p>one</p><p>two</p>" would become "onetwo"). Matched raw and entity-encoded, since
 * stored bodies contain both. Same boundary rule as HTML_TAGS, because pasted email threads collide
 * with these names too: without it, `<p.balster@hotmail.com>` (a real production row) reads as a
 * <p> tag and the address vanishes.
 */
const BLOCK_TAGS = new RegExp(`(<|&lt;)\\/?(${BLOCK_ELEMENTS})(?=[\\s\\/>]|&gt;)[^>]*?(>|&gt;)`, 'gi');

/**
 * A complete tag whose name is a real HTML element.
 *
 * The element names are listed rather than inferred, parsing is done with a regex rather than the DOM,
 * and a name only counts when followed by a space, "/" or ">" — each guard exists because a looser
 * rule deletes text a customer typed. A generic <word> match eats the letter-template placeholders
 * that survive into violation bodies (`Dear <NAME>, the issue <ISSUE> has been resolved`). An HTML
 * parser treats a bare "<" as an open tag and drops everything after it ("temp <50 degrees ok"
 * becomes "temp "). And pasted email threads carry addresses in angle brackets
 * (`From: Martin Sukadi <n.starratt@comcast.net>`), so without the boundary check
 * `<b.smith@example.com>` reads as a bold tag and vanishes.
 */
const HTML_TAGS = /<\/?(?:a|abbr|article|aside|b|blockquote|body|br|button|caption|center|code|col|colgroup|dd|del|div|dl|dt|em|figcaption|figure|font|footer|form|h[1-6]|head|header|hr|html|i|img|input|ins|label|li|link|meta|nav|ol|option|p|pre|s|script|section|select|small|span|strike|strong|style|sub|sup|table|tbody|td|textarea|tfoot|th|thead|title|tr|u|ul)(?=[\s\/>])[^>]*>/gi;

const HTML_COMMENTS = /<!--[\s\S]*?-->/g;

/**
 * Namespaced markup that Word and Outlook paste in (`<o:p>`, `<v:shapetype …>` — both in production
 * rows). A colon in the tag name is the signal, with one carve-out: the same Outlook paste also
 * produces `<mailto:ssmcghie@gmail.com>`, so a match may never cross an `@` — Office namespace tags
 * contain no addresses, mailto artifacts always do.
 */
const NAMESPACED_TAGS = /<\/?[a-z]+:[a-z][^>@]*>/gi;

/**
 * Private-use-area code points: font-specific glyphs that paste in from Word and Outlook and render
 * as tofu boxes in a spreadsheet. Carried over from the CSV encoder this replaced.
 */
const PRIVATE_USE_AREA = /[\uE000-\uF8FF\u{F0000}-\u{FFFFD}\u{100000}-\u{10FFFD}]/gu;

export function toPlainText(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    const stripped = String(value)
            .replace(RAW_TEXT_ELEMENTS, '')
            .replace(BLOCK_TAGS, ' ')
            .replace(HTML_COMMENTS, '')
            .replace(NAMESPACED_TAGS, '')
            .replace(HTML_TAGS, '');

    return decodeEntities(stripped)
            .replace(PRIVATE_USE_AREA, '')
            .replace(/\u00A0/g, ' ')
            .replace(/[\r\n\t]+/g, ' ')
            .replace(/ {2,}/g, ' ')
            .trim();
}

/**
 * Block-level tags written as real markup (never entity-encoded — `&lt;p&gt;` renders as literal text
 * and carries no line structure). BLOCK_ELEMENTS with the flattener's boundary rule, for the same
 * reason: `<p.balster@hotmail.com>` is an address, not a paragraph. Adds `pre` and `hr`, which lay out
 * lines without separating words — a rich text editor "Preformatted" body is a lone `<pre>` whose
 * newlines nl2br would double.
 *
 * Deliberately not /g: a /g regex keeps `lastIndex` between `test()` calls, so the answer would depend
 * on the previous value tested.
 */
const BLOCK_MARKUP = new RegExp(`<\\/?(${BLOCK_ELEMENTS}|pre|hr)(?=[\\s\\/>])[^>]*>`, 'i');

/**
 * NAMESPACED_TAGS without /g, for the same `lastIndex` reason. Word and Outlook markup is HTML source
 * whose newlines are formatting, not line breaks, so it renders as-is like block markup does.
 */
const OFFICE_MARKUP = new RegExp(NAMESPACED_TAGS.source, 'i');

/**
 * Whether a stored body lays out its own lines with block markup, deciding whether a view renders it
 * as-is or runs it through `nl2br` first.
 *
 * Only block tags count. Legacy textarea bodies mix inline tags into newline-formatted plaintext
 * (`<b>Nature of Violation:</b>\nOil stains…`, still thousands of production rows), and treating
 * those as HTML skips `nl2br` and collapses every paragraph into one — while the violation email,
 * which always runs `nl2br()`, shows them correctly (SD-9522). Rich text editor bodies always wrap
 * their content in `<p>`/`<div>`/`<br>`, so they still render untouched and are not double-spaced.
 * Word/Outlook paste markup (`<o:p>`) counts too, even without a block tag around it.
 */
export function containsBlockMarkup(value: string | null | undefined): boolean {
    if (!value) {
        return false;
    }

    const text = String(value);

    return BLOCK_MARKUP.test(text) || OFFICE_MARKUP.test(text);
}

/**
 * Newlines to `<br />`, keeping the newline itself — the client twin of PHP's `nl2br()`, which the
 * violation email runs on the same bodies.
 */
export function newlinesToBreaks(value: string): string {
    return value.replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1<br />$2');
}

/**
 * A stored body as HTML ready to render (or to load into the rich text editor): returned as-is when
 * block markup already lays out its lines, otherwise with its newlines turned into `<br />` — the
 * same line breaks the emailed version shows.
 */
export function toBodyHtml(value: string | null | undefined): string {
    if (!value) {
        return '';
    }

    return containsBlockMarkup(value) ? value : newlinesToBreaks(value);
}

/**
 * Decode entities via a textarea: its content is raw text, so this decodes every entity — named or
 * numeric — without parsing tags, and cannot turn an encoded fragment back into a live one. Runs
 * after tag removal for that reason, the same order the server-side formatter uses.
 */
function decodeEntities(value: string): string {
    if (value.indexOf('&') === -1) {
        return value;
    }

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;

    return textarea.value;
}
